import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { bettingDayBounds } from "@/lib/picks";
import {
  Activity, CheckCircle2, Clock, AlertTriangle, XCircle, ExternalLink,
  Trophy, Star, Layers, FileText, Users, DollarSign, Zap, RefreshCw,
  Send, MessageSquare, Mail, Twitter, ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Health = "healthy" | "warning" | "stale" | "failed" | "idle";

const HEALTH_STYLES: Record<Health, string> = {
  healthy: "bg-success/15 text-success border-success/40",
  warning: "bg-warning/15 text-warning border-warning/40",
  stale:   "bg-muted text-muted-foreground border-border",
  failed:  "bg-danger/15 text-danger border-danger/40",
  idle:    "bg-secondary/40 text-muted-foreground border-border",
};

const HEALTH_ICONS: Record<Health, React.ReactNode> = {
  healthy: <CheckCircle2 className="w-3 h-3" />,
  warning: <AlertTriangle className="w-3 h-3" />,
  stale:   <Clock className="w-3 h-3" />,
  failed:  <XCircle className="w-3 h-3" />,
  idle:    <Clock className="w-3 h-3" />,
};

function StatusPill({ status, label }: { status: Health; label?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize", HEALTH_STYLES[status])}>
      {HEALTH_ICONS[status]}{label ?? status}
    </span>
  );
}

function Metric({ label, value, hint, status }: { label: string; value: React.ReactNode; hint?: string; status?: Health }) {
  return (
    <Card className="te-card">
      <CardContent className="p-4 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
          {status && <StatusPill status={status} />}
        </div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

type PipelineStep = {
  key: string;
  label: string;
  status: Health;
  detail?: string;
};

function Pipeline({ steps }: { steps: PipelineStep[] }) {
  return (
    <div className="te-card p-4">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-primary" />
        <h3 className="font-semibold">Daily Publishing Pipeline</h3>
      </div>
      <div className="grid gap-2 md:flex md:items-stretch md:gap-0">
        {steps.map((s, i) => (
          <div key={s.key} className="flex md:flex-1 items-stretch">
            <div className="flex-1 rounded-lg border border-border/60 bg-muted/10 p-3 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium truncate">{s.label}</span>
                <StatusPill status={s.status} />
              </div>
              {s.detail && <div className="text-[11px] text-muted-foreground truncate">{s.detail}</div>}
            </div>
            {i < steps.length - 1 && (
              <div className="hidden md:flex items-center px-1 text-muted-foreground/40">
                <ArrowRight className="w-4 h-4" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface OverviewState {
  publishedToday: number;
  draftsPending: number;
  top5Today: number;
  freePickAssigned: boolean;
  parlayPublished: boolean;
  parlayResult: string | null;
  parlayPurchasesToday: number;
  pendingSettlement: number;
  articlesPending: number;
  articlesPublishedToday: number;
  proSubscribers: number;
  activeOverrides: number;
  lastOddsSync: string | null;
  lastTop5At: string | null;
  loading: boolean;
}

const initial: OverviewState = {
  publishedToday: 0, draftsPending: 0, top5Today: 0, freePickAssigned: false,
  parlayPublished: false, parlayResult: null, parlayPurchasesToday: 0,
  pendingSettlement: 0, articlesPending: 0, articlesPublishedToday: 0,
  proSubscribers: 0, activeOverrides: 0,
  lastOddsSync: null, lastTop5At: null, loading: true,
};

function freshness(iso: string | null, warnMin: number, staleMin: number): Health {
  if (!iso) return "stale";
  const ageMin = (Date.now() - new Date(iso).getTime()) / 60000;
  if (ageMin <= warnMin) return "healthy";
  if (ageMin <= staleMin) return "warning";
  return "stale";
}

function fmtAgo(iso: string | null) {
  if (!iso) return "Never";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AdminOverview({ onNavigateTab }: { onNavigateTab?: (tab: string) => void }) {
  const [s, setS] = useState<OverviewState>(initial);

  async function load() {
    setS((p) => ({ ...p, loading: true }));
    const b = bettingDayBounds();
    const todayStart = b.start;
    const todayEnd = b.end;

    const [
      publishedTodayQ,
      draftsQ,
      top5Q,
      freeQ,
      parlayQ,
      parlayPurchasesQ,
      pendingSettleQ,
      articlesPendingQ,
      articlesPublishedTodayQ,
      proSubsQ,
      overridesQ,
      lastTop5Q,
      lastOddsQ,
    ] = await Promise.all([
      supabase.from("picks").select("id", { count: "exact", head: true })
        .eq("status", "published").gte("published_at", todayStart).lt("published_at", todayEnd).is("deleted_at", null),
      supabase.from("picks").select("id", { count: "exact", head: true })
        .eq("status", "draft").is("deleted_at", null),
      supabase.from("picks").select("id", { count: "exact", head: true })
        .eq("status", "published").eq("is_top5", true).gte("published_at", todayStart).lt("published_at", todayEnd).is("deleted_at", null),
      supabase.from("picks").select("id").eq("is_free_pick", true).eq("status", "published")
        .gte("published_at", todayStart).lt("published_at", todayEnd).is("deleted_at", null).limit(1),
      supabase.from("parlay_of_day").select("status, result, published_at")
        .gte("betting_date", todayStart.slice(0, 10)).lte("betting_date", todayEnd.slice(0, 10)).limit(1),
      supabase.from("parlay_purchase_entitlements").select("id", { count: "exact", head: true })
        .gte("created_at", todayStart).lt("created_at", todayEnd),
      supabase.from("picks").select("id", { count: "exact", head: true })
        .eq("status", "published").eq("result", "pending").is("deleted_at", null)
        .lt("game_start_time", new Date().toISOString()),
      supabase.from("pick_articles").select("id", { count: "exact", head: true }).neq("status", "published"),
      supabase.from("pick_articles").select("id", { count: "exact", head: true })
        .eq("status", "published").gte("published_at", todayStart).lt("published_at", todayEnd),
      supabase.from("subscriptions").select("id", { count: "exact", head: true })
        .neq("tier", "free").in("subscription_status", ["active", "trialing"]),
      supabase.from("user_subscription_overrides").select("id", { count: "exact", head: true })
        .is("revoked_at", null),
      supabase.from("picks").select("created_at").eq("is_top5", true).order("created_at", { ascending: false }).limit(1),
      supabase.from("api_usage_logs").select("created_at").eq("provider", "odds_api")
        .order("created_at", { ascending: false }).limit(1),
    ]);

    const parlay = parlayQ.data?.[0] as any | undefined;

    setS({
      publishedToday: publishedTodayQ.count ?? 0,
      draftsPending: draftsQ.count ?? 0,
      top5Today: top5Q.count ?? 0,
      freePickAssigned: !!(freeQ.data && freeQ.data.length),
      parlayPublished: parlay?.status === "published",
      parlayResult: parlay?.result ?? null,
      parlayPurchasesToday: parlayPurchasesQ.count ?? 0,
      pendingSettlement: pendingSettleQ.count ?? 0,
      articlesPending: articlesPendingQ.count ?? 0,
      articlesPublishedToday: articlesPublishedTodayQ.count ?? 0,
      proSubscribers: (proSubsQ.count ?? 0) + (overridesQ.count ?? 0),
      activeOverrides: overridesQ.count ?? 0,
      lastOddsSync: (lastOddsQ.data?.[0] as any)?.created_at ?? null,
      lastTop5At: (lastTop5Q.data?.[0] as any)?.created_at ?? null,
      loading: false,
    });
  }

  useEffect(() => { load(); }, []);

  const oddsHealth = freshness(s.lastOddsSync, 360, 1440); // ok <6h, warn <24h
  const top5Health = freshness(s.lastTop5At, 720, 1440);

  const top5Status: Health = s.top5Today >= 5 ? "healthy" : s.top5Today > 0 ? "warning" : "idle";
  const parlayStatus: Health = s.parlayPublished ? "healthy" : "idle";
  const freeStatus: Health = s.freePickAssigned ? "healthy" : "warning";
  const settlementStatus: Health = s.pendingSettlement === 0 ? "healthy" : s.pendingSettlement < 5 ? "warning" : "failed";
  const draftsStatus: Health = s.draftsPending === 0 ? "idle" : "warning";

  const pipeline: PipelineStep[] = [
    { key: "odds", label: "Odds Pulled", status: oddsHealth, detail: `Last: ${fmtAgo(s.lastOddsSync)}` },
    { key: "ai", label: "AI Picks Generated", status: top5Health, detail: `Last gen: ${fmtAgo(s.lastTop5At)}` },
    { key: "review", label: "Admin Review", status: draftsStatus, detail: `${s.draftsPending} drafts pending` },
    { key: "articles", label: "Articles Generated", status: s.articlesPublishedToday > 0 ? "healthy" : s.articlesPending > 0 ? "warning" : "idle", detail: `${s.articlesPublishedToday} today · ${s.articlesPending} pending` },
    { key: "top5", label: "Top 5 Published", status: top5Status, detail: `${s.top5Today}/5 published` },
    { key: "parlay", label: "Parlay Published", status: parlayStatus, detail: s.parlayPublished ? "Live" : "Not yet" },
    { key: "notify", label: "Notifications Sent", status: "idle", detail: "Not configured" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Operations Overview</h2>
          <p className="text-sm text-muted-foreground">Daily publishing health at a glance.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={s.loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", s.loading && "animate-spin")} />Refresh
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/" target="_blank"><ExternalLink className="w-4 h-4 mr-2" />Open Public View</Link>
          </Button>
        </div>
      </div>

      <Pipeline steps={pipeline} />

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Today</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Metric label="Picks Published" value={s.publishedToday} status={s.publishedToday > 0 ? "healthy" : "idle"} />
          <Metric label="Top 5 Status" value={`${s.top5Today}/5`} status={top5Status} hint={`Last gen ${fmtAgo(s.lastTop5At)}`} />
          <Metric label="Parlay Status" value={s.parlayPublished ? "Published" : "Not Set"} status={parlayStatus} hint={s.parlayResult && s.parlayResult !== "pending" ? `Result: ${s.parlayResult}` : undefined} />
          <Metric label="Free Pick" value={s.freePickAssigned ? "Assigned" : "Not Set"} status={freeStatus} />
          <Metric label="Pending Drafts" value={s.draftsPending} status={draftsStatus} />
          <Metric label="Pending Articles" value={s.articlesPending} status={s.articlesPending > 0 ? "warning" : "idle"} />
          <Metric label="Pending Settlement" value={s.pendingSettlement} status={settlementStatus} hint="Past start, unsettled" />
          <Metric label="Parlay Purchases" value={s.parlayPurchasesToday} status="idle" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">System Health</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Metric label="Last Odds Sync" value={fmtAgo(s.lastOddsSync)} status={oddsHealth} />
          <Metric label="Last Top 5 Generation" value={fmtAgo(s.lastTop5At)} status={top5Health} />
          <Metric label="Active Pro Users" value={s.proSubscribers} hint={`${s.activeOverrides} overrides`} status="healthy" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Revenue & Subscriptions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric label="Active Pro" value={s.proSubscribers} status="healthy" />
          <Metric label="MRR" value="—" hint="Stripe integration" status="idle" />
          <Metric label="Parlay Sales Today" value={s.parlayPurchasesToday} status="idle" />
          <Metric label="Conversion Rate" value="—" hint="Coming soon" status="idle" />
        </div>
      </div>

      <Card className="te-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="w-4 h-4 text-accent" />Distribution
            <Badge variant="secondary" className="ml-2 text-[10px]">Coming soon</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { icon: <Twitter className="w-4 h-4" />, label: "Post to X" },
              { icon: <Mail className="w-4 h-4" />, label: "Email Alert" },
              { icon: <Zap className="w-4 h-4" />, label: "Push Notification" },
              { icon: <MessageSquare className="w-4 h-4" />, label: "Discord / Telegram" },
            ].map((d) => (
              <Button key={d.label} variant="outline" size="sm" disabled className="justify-start opacity-60">
                {d.icon}<span className="ml-2">{d.label}</span>
              </Button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Notification infrastructure not yet wired. UI is reserved for future automation.</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Button variant="outline" onClick={() => onNavigateTab?.("picks")}><Layers className="w-4 h-4 mr-2" />Manage Picks</Button>
        <Button variant="outline" onClick={() => onNavigateTab?.("parlays")}><Trophy className="w-4 h-4 mr-2" />Parlay Ops</Button>
        <Button asChild variant="outline"><Link to="/admin/articles"><FileText className="w-4 h-4 mr-2" />Articles</Link></Button>
        <Button asChild variant="outline"><Link to="/admin/users"><Users className="w-4 h-4 mr-2" />Users</Link></Button>
      </div>
    </div>
  );
}
